"use client";

import type {
  DispatchBoard,
  DispatchNurse,
  DispatchNurseRoute,
} from "@vellum/vellum-api";
import * as maplibregl from "maplibre-gl";
import {
  type ExpressionSpecification,
  type GeoJSONSource,
  type LngLatBoundsLike,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";

import {
  type BoardIndex,
  type FacilityMark,
  JOB_TYPE_LABELS,
  coordinatesOf,
  facilityMark,
  formatClock,
  initials,
  nursePosition,
} from "./board";

// Grayscale vector basemap, no API key, so the routes are the loudest thing.
const BASEMAP_STYLE = "https://tiles.openfreemap.org/styles/positron";
/** The style's OpenMapTiles vector source, reused for the state lines. */
const BASEMAP_VECTOR_SOURCE = "openmaptiles";
const CONTINENTAL_US: LngLatBoundsLike = [
  [-125, 24],
  [-66, 50],
];

// States are told apart by fill lightness, a glyph, or a dash, never hue alone.
const INK = "#1c1917";
const LATE = "#b45309";
const MUTED = "#a8a29e";
const DONE = "#e7e5e4";

const FACILITY_ICONS: Record<FacilityMark, { fill: string; glyph?: string }> = {
  late: { fill: LATE, glyph: "!" },
  unassigned: { fill: "#ffffff" },
  open: { fill: INK },
  done: { fill: DONE },
};
// Louder marks draw on top where pins overlap.
const MARK_ORDER: Record<FacilityMark, number> = {
  done: 0,
  open: 1,
  unassigned: 2,
  late: 3,
};

const NURSE_STYLES: Record<DispatchNurse["state"], string> = {
  on_site: "bg-stone-900 text-white border-stone-900",
  between_stops: "bg-white text-stone-900 border-stone-900 border-dashed",
  not_started: "bg-white text-stone-600 border-stone-400",
  done: "bg-stone-200 text-stone-500 border-stone-300",
};

function squareIcon(fill: string, glyph?: string): ImageData {
  const size = 22;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = fill;
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2.5;
  ctx.fillRect(3, 3, size - 6, size - 6);
  ctx.strokeRect(3, 3, size - 6, size - 6);
  if (glyph) {
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 14px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(glyph, size / 2, size / 2 + 1);
  }
  return ctx.getImageData(0, 0, size, size);
}

function escapeHtml(text: string): string {
  return text.replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char]!,
  );
}

function popupHtml(title: string, lines: string[]): string {
  return `<div class="text-sm"><div class="font-semibold text-gray-900">${escapeHtml(title)}</div>${lines
    .map((line) => `<div class="text-gray-600">${escapeHtml(line)}</div>`)
    .join("")}</div>`;
}

type FacilityProps = { id: string; name: string; icon: string; order: number };

function facilityCollection(
  index: BoardIndex,
): GeoJSON.FeatureCollection<GeoJSON.Point, FacilityProps> {
  const features: GeoJSON.Feature<GeoJSON.Point, FacilityProps>[] = [];
  for (const [id, jobs] of index.jobsByFacility) {
    const facility = index.facilities.get(id);
    const coordinates = coordinatesOf(facility);
    if (!facility || !coordinates) continue;
    const mark = facilityMark(jobs);
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates },
      properties: {
        id,
        name: facility.name,
        icon: `facility-${mark}`,
        order: MARK_ORDER[mark],
      },
    });
  }
  return { type: "FeatureCollection", features };
}

type LegProps = { nurseId: string; jobId: string; state: string };

function legCollection(
  board: DispatchBoard | null,
  index: BoardIndex,
): GeoJSON.FeatureCollection<GeoJSON.LineString, LegProps> {
  const features: GeoJSON.Feature<GeoJSON.LineString, LegProps>[] = [];
  for (const nurse of board?.nurses ?? []) {
    const home: [number, number] | null =
      nurse.home_lat != null && nurse.home_long != null
        ? [nurse.home_long, nurse.home_lat]
        : null;
    for (const leg of nurse.legs) {
      const from = leg.from_facility_id
        ? coordinatesOf(index.facilities.get(leg.from_facility_id))
        : home;
      const to = coordinatesOf(index.facilities.get(leg.to_facility_id));
      if (!from || !to || (from[0] === to[0] && from[1] === to[1])) continue;
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: [from, to] },
        properties: { nurseId: nurse.id, jobId: leg.job_id, state: leg.state },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** The highlighted nurse's legs along the actual roads, styled like the straight ones. */
function roadCollection(
  route: DispatchNurseRoute | null,
  board: DispatchBoard | null,
): GeoJSON.FeatureCollection<GeoJSON.LineString, LegProps> {
  const nurse = board?.nurses.find(
    (candidate) => candidate.id === route?.nurse_id,
  );
  const states = new Map(nurse?.legs.map((leg) => [leg.job_id, leg.state]));
  return {
    type: "FeatureCollection",
    features: (route?.legs ?? []).map((leg) => ({
      type: "Feature",
      geometry: { type: "LineString", coordinates: leg.points },
      properties: {
        nurseId: route!.nurse_id,
        jobId: leg.job_id,
        state: states.get(leg.job_id) ?? "planned",
      },
    })),
  };
}

type PointProps = { id: string; name: string };

/** Homes of nurses already out; a nurse who hasn't started is drawn at home anyway. */
function homeCollection(
  board: DispatchBoard | null,
): GeoJSON.FeatureCollection<GeoJSON.Point, PointProps> {
  return {
    type: "FeatureCollection",
    features: (board?.nurses ?? [])
      .filter(
        (nurse) =>
          nurse.state !== "not_started" &&
          nurse.home_lat != null &&
          nurse.home_long != null,
      )
      .map((nurse) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [nurse.home_long!, nurse.home_lat!],
        },
        properties: { id: nurse.id, name: nurse.name },
      })),
  };
}

function idleCollection(
  board: DispatchBoard | null,
): GeoJSON.FeatureCollection<GeoJSON.Point, PointProps> {
  return {
    type: "FeatureCollection",
    features: (board?.idle_nurses ?? []).map((nurse) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [nurse.long, nurse.lat] },
      properties: { id: nurse.id, name: nurse.name },
    })),
  };
}

const LEG_LAYERS = ["legs-traveled", "legs-planned", "legs-next"] as const;
const NURSE_POINT_LAYERS = ["homes"] as const;

function selectionOpacity(
  selectedNurseId: string | null,
  key: string,
): number | ExpressionSpecification {
  return selectedNurseId
    ? ["case", ["==", ["get", key], selectedNurseId], 1, 0.12]
    : 1;
}

/** As selectionOpacity, but a selected leg drawn along the road hides its straight line. */
function legOpacity(
  selectedNurseId: string | null,
  roadJobIds: string[],
): number | ExpressionSpecification {
  if (!selectedNurseId) return 1;
  return [
    "case",
    ["!=", ["get", "nurseId"], selectedNurseId],
    0.12,
    ["in", ["get", "jobId"], ["literal", roadJobIds]],
    0,
    1,
  ];
}

/** The same three looks for straight legs and roads: driven, next, later. */
function addLegLayers(map: maplibregl.Map, source: string, prefix: string) {
  map.addLayer({
    id: `${prefix}-traveled`,
    type: "line",
    source,
    filter: ["==", ["get", "state"], "traveled"],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": MUTED, "line-width": 2 },
  });
  map.addLayer({
    id: `${prefix}-planned`,
    type: "line",
    source,
    filter: ["==", ["get", "state"], "planned"],
    layout: { "line-join": "round" },
    paint: { "line-color": INK, "line-width": 2, "line-dasharray": [2, 2] },
  });
  map.addLayer({
    id: `${prefix}-next`,
    type: "line",
    source,
    filter: ["==", ["get", "state"], "next"],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": INK, "line-width": 4 },
  });
}

type Props = {
  board: DispatchBoard | null;
  index: BoardIndex;
  selectedNurseId: string | null;
  onSelectNurse: (nurseId: string | null) => void;
  /**
   * The selected nurse's legs along the actual roads. Legs it doesn't cover
   * stay straight lines.
   */
  selectedRoute: DispatchNurseRoute | null;
  /**
   * The map re-frames to the board's facilities whenever this changes, so
   * pass something that changes with the regions shown, not with each poll.
   */
  frameKey: string | null;
};

export function DispatchMap({
  board,
  index,
  selectedNurseId,
  onSelectNurse,
  selectedRoute,
  frameKey,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markers = useRef(new Map<string, maplibregl.Marker>());
  const framedKey = useRef<string | null>(null);
  // Map event handlers are bound once; read the latest props through refs.
  const onSelectRef = useRef(onSelectNurse);
  onSelectRef.current = onSelectNurse;
  const indexRef = useRef(index);
  indexRef.current = index;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const map = new maplibregl.Map({
      container: container.current!,
      style: BASEMAP_STYLE,
      bounds: CONTINENTAL_US,
      attributionControl: { compact: true },
    });
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right",
    );
    mapRef.current = map;

    map.on("load", () => {
      for (const [mark, { fill, glyph }] of Object.entries(FACILITY_ICONS)) {
        map.addImage(`facility-${mark}`, squareIcon(fill, glyph));
      }
      const empty = (): GeoJSON.FeatureCollection => ({
        type: "FeatureCollection",
        features: [],
      });
      map.addSource("legs", { type: "geojson", data: empty() });
      map.addSource("roads", { type: "geojson", data: empty() });
      map.addSource("homes", { type: "geojson", data: empty() });
      map.addSource("idle", { type: "geojson", data: empty() });
      map.addSource("facilities", { type: "geojson", data: empty() });

      // The basemap only draws state lines faintly from z8; regions follow
      // state lines, so show them at every zoom, as the coverage map does.
      map.addLayer({
        id: "state-lines",
        type: "line",
        source: BASEMAP_VECTOR_SOURCE,
        "source-layer": "boundary",
        filter: [
          "all",
          ["==", ["get", "admin_level"], 4],
          ["!=", ["get", "maritime"], 1],
        ],
        paint: {
          "line-color": MUTED,
          "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.75, 8, 1.5],
        },
      });
      addLegLayers(map, "legs", "legs");
      addLegLayers(map, "roads", "roads");
      map.addLayer({
        id: "idle",
        type: "circle",
        source: "idle",
        paint: {
          "circle-color": MUTED,
          "circle-radius": 4,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1,
        },
      });
      map.addLayer({
        id: "homes",
        type: "circle",
        source: "homes",
        paint: {
          "circle-color": "#ffffff",
          "circle-radius": 4,
          "circle-stroke-color": INK,
          "circle-stroke-width": 1.5,
        },
      });
      map.addLayer({
        id: "facilities",
        type: "symbol",
        source: "facilities",
        layout: {
          "icon-image": ["get", "icon"],
          "icon-size": ["interpolate", ["linear"], ["zoom"], 4, 0.6, 10, 0.9],
          "icon-allow-overlap": true,
          "symbol-sort-key": ["get", "order"],
          // Names only once zoomed in far enough for them not to pile up.
          "text-field": ["step", ["zoom"], "", 9, ["get", "name"]],
          "text-size": 11,
          "text-offset": [0, 1.3],
          "text-anchor": "top",
          "text-optional": true,
        },
        paint: {
          "text-color": INK,
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        },
      });

      map.on("click", "facilities", (event) => {
        const id = event.features?.[0]?.properties?.id as string | undefined;
        const facility = id ? indexRef.current.facilities.get(id) : undefined;
        if (!facility) return;
        const lines = (
          indexRef.current.jobsByFacility.get(facility.id) ?? []
        ).map((job) => {
          const when = job.due_at
            ? formatClock(job.due_at, facility.timezone)
            : "ASAP";
          const who = job.nurse_id
            ? (indexRef.current.nurses.get(job.nurse_id)?.name ?? "Assigned")
            : "Unassigned";
          const type = JOB_TYPE_LABELS[job.job_type] ?? job.job_type;
          return `${when} · ${type} · ${who}${job.late ? " · late" : ""}`;
        });
        new maplibregl.Popup({ closeButton: false, maxWidth: "300px" })
          .setLngLat(event.lngLat)
          .setHTML(popupHtml(facility.name, lines))
          .addTo(map);
      });
      for (const layer of ["idle", ...NURSE_POINT_LAYERS]) {
        map.on("click", layer, (event) => {
          const name = event.features?.[0]?.properties?.name as
            string | undefined;
          if (!name) return;
          new maplibregl.Popup({ closeButton: false })
            .setLngLat(event.lngLat)
            .setHTML(
              popupHtml(name, [layer === "idle" ? "No jobs today" : "Home"]),
            )
            .addTo(map);
        });
      }
      for (const layer of ["facilities", "idle", ...NURSE_POINT_LAYERS]) {
        map.on("mouseenter", layer, () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", layer, () => {
          map.getCanvas().style.cursor = "";
        });
      }
      // A click on empty map clears the selected route.
      map.on("click", (event) => {
        const hits = map.queryRenderedFeatures(event.point, {
          layers: ["facilities", "idle", "homes"],
        });
        if (hits.length === 0) onSelectRef.current(null);
      });

      setReady(true);
    });

    const ownMarkers = markers.current;
    return () => {
      ownMarkers.forEach((marker) => marker.remove());
      ownMarkers.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Sources and nurse markers follow every poll.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("facilities") as GeoJSONSource).setData(
      facilityCollection(index),
    );
    (map.getSource("legs") as GeoJSONSource).setData(
      legCollection(board, index),
    );
    (map.getSource("homes") as GeoJSONSource).setData(homeCollection(board));
    (map.getSource("idle") as GeoJSONSource).setData(idleCollection(board));

    // Nurses sharing a spot fan out to the right instead of stacking.
    const seenAt = new Map<string, number>();
    const live = new Set<string>();
    for (const nurse of board?.nurses ?? []) {
      const position = nursePosition(nurse, index.facilities);
      if (!position) continue;
      const spot = position.join(",");
      const slot = seenAt.get(spot) ?? 0;
      seenAt.set(spot, slot + 1);
      live.add(nurse.id);

      let marker = markers.current.get(nurse.id);
      if (!marker) {
        const element = document.createElement("button");
        element.type = "button";
        // Inline, so a host app without a CSS reset doesn't draw a native button.
        Object.assign(element.style, {
          background: "transparent",
          border: "0",
          padding: "0",
          cursor: "pointer",
        });
        element.appendChild(document.createElement("span"));
        element.addEventListener("click", (event) => {
          event.stopPropagation();
          onSelectRef.current(nurse.id);
        });
        marker = new maplibregl.Marker({ element, anchor: "center" });
        markers.current.set(nurse.id, marker);
        marker.setLngLat(position).addTo(map);
      }
      const element = marker.getElement();
      element.title = nurse.name;
      element.setAttribute("aria-label", `Show ${nurse.name}'s route`);
      // MapLibre positions the marker through its own classes on the outer
      // element (absolute, transformed), so all of our styling goes on the
      // badge inside; replacing the outer className sets the marker adrift.
      const badge = element.firstElementChild as HTMLElement;
      badge.textContent = initials(nurse.name);
      badge.className = [
        "flex h-7 w-7 items-center justify-center rounded-full border-2 text-[11px] font-semibold shadow-sm",
        NURSE_STYLES[nurse.state],
      ].join(" ");
      // At a facility the badge sits beside the pin; at home, on it.
      marker.setOffset(
        nurse.facility_id ? [16 + slot * 20, -14] : [slot * 20, 0],
      );
      marker.setLngLat(position);
    }
    for (const [id, marker] of markers.current) {
      if (live.has(id)) continue;
      marker.remove();
      markers.current.delete(id);
    }
  }, [ready, board, index]);

  // Only the highlighted nurse's roads are drawn; a stale route for someone
  // else (still loading the new one) is dropped rather than shown.
  const route =
    selectedRoute && selectedRoute.nurse_id === selectedNurseId
      ? selectedRoute
      : null;
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    (map.getSource("roads") as GeoJSONSource).setData(
      roadCollection(route, board),
    );
  }, [ready, route, board]);

  // The selected nurse's route stays loud; everyone else's fades back.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const roadJobIds = (route?.legs ?? []).map((leg) => leg.job_id);
    for (const layer of LEG_LAYERS) {
      map.setPaintProperty(
        layer,
        "line-opacity",
        legOpacity(selectedNurseId, roadJobIds),
      );
    }
    for (const layer of NURSE_POINT_LAYERS) {
      map.setPaintProperty(
        layer,
        "circle-opacity",
        selectionOpacity(selectedNurseId, "id"),
      );
      map.setPaintProperty(
        layer,
        "circle-stroke-opacity",
        selectionOpacity(selectedNurseId, "id"),
      );
    }
    for (const [id, marker] of markers.current) {
      const element = marker.getElement();
      const badge = element.firstElementChild as HTMLElement;
      const faded = selectedNurseId !== null && id !== selectedNurseId;
      badge.style.opacity = faded ? "0.35" : "1";
      badge.style.outline =
        id === selectedNurseId ? `3px solid ${INK}` : "none";
      badge.style.outlineOffset = "2px";
      // Inline z-index only; MapLibre's classes on this element stay intact.
      element.style.zIndex = id === selectedNurseId ? "2" : "1";
    }
  }, [ready, selectedNurseId, board, route]);

  // Frame the board's facilities on first load and when the regions change,
  // not on every poll. Homes are left out so one far-off nurse can't zoom
  // the whole board out.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || frameKey === null || framedKey.current === frameKey)
      return;
    framedKey.current = frameKey;
    const points = [...index.facilities.values()]
      .map(coordinatesOf)
      .filter((point): point is [number, number] => point !== null);
    if (points.length === 0) {
      map.fitBounds(CONTINENTAL_US, { duration: 0 });
      return;
    }
    const bounds = points.reduce(
      (box, point) => box.extend(point),
      new maplibregl.LngLatBounds(points[0], points[0]),
    );
    map.fitBounds(bounds, { padding: 60, maxZoom: 11, duration: 600 });
  }, [ready, board, index, frameKey]);

  return (
    <div
      ref={container}
      className="h-full w-full"
      role="region"
      aria-label="Map of today's facilities and nurse routes"
    />
  );
}
